import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getPublicBranding } from '../lib/api';
import { toast } from 'sonner';
import { Package, EnvelopeSimple, Lock, Eye, EyeSlash, Translate } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { isPartnerRole } from '../lib/partners';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, language, toggleLanguage, langLabel, tx } = useLanguage();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [companyBranding, setCompanyBranding] = useState(null);

  const companyId = searchParams.get('company');

  useEffect(() => {
    if (companyId) {
      getPublicBranding(companyId)
        .then(res => setCompanyBranding(res.data))
        .catch(() => {});
    }
  }, [companyId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error(tx('Please fill in all fields', 'Por favor completa todos los campos', 'Please fill in all fields'));
      return;
    }
    setLoading(true);
    try {
      const userData = await login(email, password);
      toast.success(tx('Welcome back!', '!Bienvenido!', 'Welcome back!'));
      // Warehouse role lands directly on Packages (their only allowed page)
      const role = userData?.role;
      navigate(role === 'warehouse' ? '/packages' : isPartnerRole(role) ? '/customers' : '/dashboard');
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Login failed';
      toast.error(msg);
      console.error('Login error:', error.response?.status, msg);
    } finally {
      setLoading(false);
    }
  };

  const hasCustomLogo = companyBranding?.logo_url;
  const brandColor = companyBranding?.primary_color || '#003166';

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{ background: hasCustomLogo ? `linear-gradient(135deg, ${brandColor}, ${brandColor}dd)` : undefined }}
      >
        {!hasCustomLogo && <div className="absolute inset-0 ship-gradient" />}
        {/* Decorative shapes */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />

        <div className="relative z-10 flex flex-col items-center justify-center w-full p-12">
          <div className="mb-8">
            {hasCustomLogo ? (
              <div className="bg-white rounded-2xl p-6 shadow-xl" data-testid="login-company-logo">
                <img src={companyBranding.logo_url} alt={companyBranding.company_name} className="h-32 max-w-[280px] object-contain mx-auto" />
              </div>
            ) : (
              <img src="/assets/trackmypackage-logo.png" alt="Track My Package" className="h-44 mx-auto mb-4" />
            )}
            <p className="text-white/80 text-center mt-4 text-lg">
              {hasCustomLogo ? companyBranding.company_name : 'Shipping & Logistics Solutions'}
            </p>
          </div>

          <div className="text-white/90 text-center space-y-4 max-w-sm">
            <div className="flex items-center gap-3 justify-center">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Package size={20} />
              </div>
              <span>Rastreo en tiempo real / Real-time tracking</span>
            </div>
            <div className="flex items-center gap-3 justify-center">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <span className="text-sm font-bold">$</span>
              </div>
              <span>Facturacion simple / Simple invoicing</span>
            </div>
          </div>

          <div className="absolute bottom-8 text-white/60 text-sm text-center">
            {hasCustomLogo ? (
              <a href="https://trackmypackage.app" target="_blank" rel="noopener noreferrer" className="hover:text-white/80 transition-colors" data-testid="powered-by-badge">
                Powered by <span className="font-semibold">Track My Package</span>
              </a>
            ) : (
              <p>trackmypackage.app</p>
            )}
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Language Toggle */}
          <div className="flex justify-end mb-6">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="language-toggle-login"
            >
              <Translate size={18} />
              <span>{langLabel}</span>
            </button>
          </div>

          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            {hasCustomLogo ? (
              <img src={companyBranding.logo_url} alt={companyBranding.company_name} className="h-20 object-contain" data-testid="login-company-logo-mobile" />
            ) : (
              <img src="/assets/trackmypackage-logo.png" alt="Track My Package" className="h-20" />
            )}
          </div>

          <h1
            className="text-3xl font-bold text-gray-900 tracking-wide mb-2"
            style={{ fontFamily: 'Bebas Neue' }}
          >
            {t('welcomeBack')}
          </h1>
          <p className="text-gray-600 mb-8">
            {t('signInDesc')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="form-label">{t('email')}</Label>
              <div className="relative">
                <EnvelopeSimple
                  size={20}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('enterEmail')}
                  className="pl-10 rounded-lg"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="form-label">{t('password')}</Label>
              <div className="relative">
                <Lock
                  size={20}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('enterPassword')}
                  className="pl-10 pr-10 rounded-lg"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full ship-gradient hover:opacity-90 rounded-lg font-semibold text-white py-3"
              data-testid="login-submit-btn"
            >
              {loading ? (tx('Signing in...', 'Iniciando...', 'Signing in...')) : t('signIn')}
            </Button>
          </form>

          <p className="mt-4 text-center text-gray-600 text-sm">
            {tx('Want your own account?', 'Quieres tu propia cuenta?', 'Vous voulez votre propre compte ?')}{' '}
            <a
              href="https://sqawk.app/book/MrPositivity"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#0077FF] font-semibold hover:underline"
              data-testid="schedule-call-link"
            >
              {tx('Schedule a Call', 'Agenda una Llamada', 'Planifier un Appel')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
